import { useEffect, useState, useMemo } from "react";
import { useLocation, useParams, useOutletContext } from "react-router-dom";
import { apiService } from "@/services/apiService";
import { appConfigService } from "@/services/appConfigService";
import type {
  IGenericPageDto,
  IEngagementDetailsPageDto,
  IErrorResponse,
} from "@/types/filteredContentTypes";
import { Breadcrumb } from "@/components/shared/Breadcrumb/Breadcrumb";
import { MarkdownContent } from "@/components/shared/Markdown/MarkdownContent";
import {
  extractH2Headings,
  generateHeadingSlug,
} from "@/components/shared/Markdown/components/HeadingRenderers/HeadingRenderers";
import { ErrorPage } from "@/pages/ErrorPage/ErrorPage";
import { Section } from "./ContentPageContent/Section/Section";
import { EligibilitySection } from "./ContentPageContent/EligibilitySection/EligibilitySection";
import { ContentPageHeader } from "./ContentPageHeader/ContentPageHeader";
import { InThisArticle } from "@/components/shared/InThisArticle/InThisArticle";
import { useTranslations } from "@/hooks/useTranslations";
import { useCurrentPage } from "@/hooks/useCurrentPage";
import { useSupportedLocale } from "@/hooks/useSupportedLocale";
import styles from "./ContentPage.module.scss";

type PageData = IGenericPageDto | IEngagementDetailsPageDto;

interface ISectionItem {
  id: string;
  title: string;
}

interface IOutletContext {
  setSections: (sections: ISectionItem[]) => void;
}

function getSectionSlugs(sections: IEngagementDetailsPageDto["children"]): string[] {
  const baseSlugs = sections.map(
    (section) =>
      generateHeadingSlug(section.title) || generateHeadingSlug(section.name) || section.key
  );
  const totals = new Map<string, number>();

  for (const slug of baseSlugs) {
    totals.set(slug, (totals.get(slug) ?? 0) + 1);
  }

  const occurrences = new Map<string, number>();
  return baseSlugs.map((slug) => {
    if ((totals.get(slug) ?? 0) === 1) {
      return slug;
    }

    const index = (occurrences.get(slug) ?? 0) + 1;
    occurrences.set(slug, index);
    return `${slug}-${index}`;
  });
}

export function ContentPage(): React.ReactNode {
  const location = useLocation();
  const params = useParams();
  const localeParam = params.locale;
  const { isLocaleValid, isLoading: isLocaleCheckLoading } = useSupportedLocale(localeParam);
  const locale = isLocaleValid ? (localeParam || "en-US") : "en-US";
  const pathname = location.pathname;
  const { setSections } = useOutletContext<IOutletContext>();
  const { t } = useTranslations();
  const { setPageId } = useCurrentPage();

  const [pageData, setPageData] = useState<PageData | null>(null);
  const [errorResponse, setErrorResponse] = useState<IErrorResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedSectionId, setCopiedSectionId] = useState<string | null>(null);
  const [mediaBaseUrl, setMediaBaseUrl] = useState<string>("");
  useEffect(() => {
    let isCancelled = false;

    async function loadConfig(): Promise<void> {
      try {
        const config = await appConfigService.getAppEnvironmentData();
        if (!isCancelled) {
          setMediaBaseUrl(config.mediaBaseUrl);
        }
      } catch (err) {
        console.error("Failed to load config:", err);
      }
    }
    void loadConfig();

    return (): void => {
      isCancelled = true;
    };
  }, []);

  function copySectionLink(sectionId: string): void {
    const url = `${window.location.origin}${pathname}#${sectionId}`;
    void navigator.clipboard.writeText(url);
    setCopiedSectionId(sectionId);
  }

  useEffect(() => {
    let isCancelled = false;

    async function loadPageData(): Promise<void> {
      try {
        if (!isCancelled) {
          setIsLoading(true);
          setErrorResponse(null);
        }

        // If the locale segment isn't valid, treat it as an unknown route and avoid calling the API.
        if (!isLocaleValid) {
          if (!isCancelled) {
            setPageData(null);
          }
          return;
        }

        // Use the full pathname including locale for the API call
        const fullPath = pathname;

        // Fetch the page data - the backend will return the appropriate type
        const pageData = await apiService.getPageByUrl(fullPath, locale);

        // Check if response is an error object
        if (pageData && typeof pageData === "object" && "code" in pageData) {
          if (!isCancelled) {
            setErrorResponse(pageData as IErrorResponse);
            setPageData(null);
          }
        } else {
          const typedPageData = pageData as PageData;
          if (!isCancelled) {
            setPageData(typedPageData);
            setErrorResponse(null);
          }

          // Persist the last successfully loaded content key for locale switching.
          if (typedPageData?.key) {
            sessionStorage.setItem('previousContentKey', typedPageData.key);
            sessionStorage.setItem('previousContentPath', pathname);
          }
        }
      } catch (err) {
        console.error("Failed to load page:", err);
        if (!isCancelled) {
          setErrorResponse({
            code: "API_FETCH_FAILED",
            message: err instanceof Error ? err.message : "Failed to load page content",
          });
          setPageData(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadPageData();

    return (): void => {
      isCancelled = true;
    };
  }, [pathname, locale, isLocaleValid]);

  // Handle hash-based scrolling after page loads
  useEffect(() => {
    // Only proceed if page has finished loading and we have data
    if (isLoading || !pageData) {
      return;
    }

    const hash = location.hash;
    if (!hash) {
      return;
    }

    const targetId = hash.substring(1); // Remove the # character

    // Try scrolling after a short delay to ensure DOM is ready
    const scrollTimeout = setTimeout(() => {
      const element = document.getElementById(targetId);
      if (element) {
        // Scroll to the element
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // If element not found, try forcing a re-navigation to trigger browser's native hash handling
        window.location.hash = "";
        window.location.hash = hash;
      }
    }, 100);

    return (): void => {
      clearTimeout(scrollTimeout);
    };
  }, [isLoading, pageData, location.hash]);

  // Build sections list for RightNav using useMemo
  const sectionsList = useMemo(() => {
    if (!pageData || isLoading || errorResponse) {
      return [];
    }

    const isEligibilityPageType = pageData.documentType === "engagementDetailsPage";
    // For eligibility pages, use children sections for the right nav
    if (isEligibilityPageType) {
      const hasContentSectionsForNav =
        "children" in pageData && (pageData as IEngagementDetailsPageDto).children.length > 0;

      if (!hasContentSectionsForNav) {
        return [];
      }

      const sections = (pageData as IEngagementDetailsPageDto).children;
      const sectionSlugs = getSectionSlugs(sections);

      return sections.map((section, index) => {
        return {
          id: sectionSlugs[index],
          title: section.title,
        };
      });
    }

    // For generic pages, extract h2 headings from markdown content
    if ("content" in pageData && pageData.content) {
      const h2Headings = extractH2Headings(pageData.content);
      return h2Headings.map((heading) => ({
        id: heading.id,
        title: heading.title,
      }));
    }

    return [];
  }, [pageData, isLoading, errorResponse]);

  // Update sections in layout whenever they change
  useEffect(() => {
    setSections(sectionsList);

    // Cleanup: clear sections when component unmounts
    return (): void => {
      setSections([]);
    };
  }, [sectionsList, setSections]);

  // Update current page ID for TopBar download functionality
  useEffect(() => {
    if (pageData && !isLoading && !errorResponse) {
      setPageId(pageData.key);
    } else {
      setPageId(undefined);
    }

    return (): void => {
      setPageId(undefined);
    };
  }, [pageData, isLoading, errorResponse, setPageId]);

  // If the URL doesn't match our expected locale route pattern, show a friendly 404.
  // This must come after hooks to keep hook call order stable.
  if (!isLocaleValid && !isLocaleCheckLoading) {
    return <ErrorPage variant="notFound" />;
  }

  // Show unauthorized page if error response received
  if (errorResponse?.code === "UNAUTHORIZED_ACCESS") {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.container}>
            <ErrorPage variant="unauthorized" />
          </div>
        </div>
      </div>
    );
  }

  // Show content not found page when content exists but is not published in the requested locale
  if (errorResponse?.code === "CONTENT_NOT_FOUND") {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.container}>
            <ErrorPage variant="contentNotFound" />
          </div>
        </div>
      </div>
    );
  }

  // Show a user-friendly error page for API/network failures.
  if (errorResponse && errorResponse.code !== "UNAUTHORIZED_ACCESS") {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.container}>
            <ErrorPage title={t("apiError.title")} message={t("apiError.message")} />
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t("contentPage.loading") || "Loading..."}</div>
      </div>
    );
  }

  // Show page not found if no page data after loading
  if (!pageData) {
    return <ErrorPage variant="notFound" />;
  }

  // Check if page has sections to render
  const hasContent =
    "content" in pageData && pageData.content && pageData.content.trim().length > 0;
  const hasSummary = pageData.summary && pageData.summary.trim().length > 0;
  const isEngagementDetailsPage = pageData.documentType === "engagementDetailsPage";

  // For eligibility pages, children are content sections (ISectionDto | IEligibilitySectionDto)
  // For generic pages, children are navigation items (IFolderDto | IGenericPageDto)
  const hasContentSections =
    isEngagementDetailsPage &&
    "children" in pageData &&
    (pageData as IEngagementDetailsPageDto).children.length > 0;

  // Determine if we should show error page - different logic for each page type
  const showErrorPage = isEngagementDetailsPage
    ? !hasContentSections // Eligibility pages need content sections
    : !hasContent && !hasSummary; // Generic pages need content or summary

  // Get parent hierarchy - both GenericPageDto and EngagementDetailsPageDto have parentHierarchy now
  const parentHierarchy = "parentHierarchy" in pageData ? pageData.parentHierarchy : null;

  const pageWrapperClassName = [
    isEngagementDetailsPage ? styles.eligibilityPage : styles.genericPage,
    showErrorPage ? styles.contentNotFound : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.container}>
          {/* Main content */}
          <div className={pageWrapperClassName}>
            <Breadcrumb hierarchy={parentHierarchy || undefined} pageName={pageData.title} />

            <InThisArticle sections={sectionsList} />

            <article className={styles.article}>
              {/* Only show header if not displaying error page */}
              {!showErrorPage && (
                <ContentPageHeader
                  title={pageData.title}
                  name={pageData.title}
                  summary={pageData.summary}
                  mediaBaseUrl={mediaBaseUrl}
                  isEngagementDetailsPage={isEngagementDetailsPage}
                />
              )}

              {/* Render top-level content if present */}
              {hasContent && (
                <MarkdownContent
                  content={(pageData as IGenericPageDto).content}
                  mediaBaseUrl={mediaBaseUrl}
                  onCopyLink={!isEngagementDetailsPage ? copySectionLink : undefined}
                  copiedSectionId={copiedSectionId}
                />
              )}

              {/* Render Sections */}
              <div className={styles.content}>
                {hasContentSections && (
                  <div className={styles.sections}>
                    {(pageData as IEngagementDetailsPageDto).children.map((section, index, sections) => {
                      const sectionId = getSectionSlugs(sections)[index];

                      // Use documentType to determine section type
                      if (section.documentType === "section") {
                        return (
                          <Section
                            key={section.key}
                            section={section}
                            sectionId={sectionId}
                            mediaBaseUrl={mediaBaseUrl}
                            copiedSectionId={copiedSectionId}
                            onCopyLink={copySectionLink}
                            isEngagementDetailsPage={isEngagementDetailsPage}
                          />
                        );
                      } else if (section.documentType === "eligibilitySection") {
                        return (
                          <EligibilitySection
                            key={section.key}
                            section={section}
                            sectionId={sectionId}
                            mediaBaseUrl={mediaBaseUrl}
                            copiedSectionId={copiedSectionId}
                            onCopyLink={copySectionLink}
                          />
                        );
                      }
                      return null;
                    })}
                  </div>
                )}
              </div>

              {/* Show error page for generic pages without content and without sections */}
              {showErrorPage && !isEngagementDetailsPage && (
                <ErrorPage variant="contentNotFound" message={t("contentPage.noContentAvailable")} />
              )}

              {/* Show error page for eligibility pages without sections */}
              {showErrorPage && isEngagementDetailsPage && (
                <ErrorPage variant="contentNotFound" message={t("contentPage.noSectionsAvailable")} />
              )}
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}

